import {
  createFileRoute,
  Outlet,
  useNavigate,
  Link,
  useRouterState,
} from '@tanstack/react-router';
import { useState, Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Home,
  FileText,
  Calendar,
  Share2,
  Users,
  LogOut,
  Plus,
  Gift,
  GraduationCap,
  Plane,
  Settings,
  Lock,
  CalendarDays,
  FileEdit,
  ChevronDown,
  User,
  PawPrint,
  MapPin,
  Menu,
  Bell,
  Sun,
  Moon,
  Monitor,
  X,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/pet/src/components/ui/dropdown-menu';
import { Switch } from '@/pet/src/components/ui/switch';
import { useNotifications } from '@/pet/src/context/NotificationsContext';
import {
  LANGUAGES,
  LANGUAGE_LABELS,
  type Language,
} from '@/translations/config';
import type { ThemePreference } from '@/pet/src/context/ThemeContext';
import { AddPetModal } from '@/pet/src/components/AddPetModal';
import { QuickAddPetsModal } from '@/pet/src/components/QuickAddPetsModal';
import { PetOnboarding } from '@/pet/src/components/PetOnboarding';
import { PetInvitationsBanner } from '@/pet/src/components/PetInvitationsBanner';
import { AddPetModalProvider } from '@/pet/src/context/AddPetModalContext';
import { SocialFeedProvider } from '@/pet/src/context/SocialFeedContext';
import { NotificationPanelProvider } from '@/context/NotificationPanelContext';
import { NotificationSidePanel } from '@/pet/src/components/NotificationSidePanel';
import type { Notification } from '@/pet/src/context/NotificationsContext';
import { useAuth } from '@/context/AuthContext';
import { usePets } from '@/pet/src/context/PetsContext';
import { useSocialConnections } from '@/pet/src/context/SocialConnectionsContext';
import { useTheme } from '@/pet/src/context/ThemeContext';
import { useOwnerOnboarding } from '@/pet/src/context/OwnerOnboardingContext';
import { usePetPhoto } from '@/pet/src/hooks/usePetPhoto';
import type { Pet } from '@/pet/src/types/pet';
import petsoLogo from '@/pet/src/assets/4c0bdef843fbe05c3bb4b14b38ec794cce2e65c9.png';
import { toast } from 'sonner';

export const Route = createFileRoute('/dashboard')({
  component: PetsoDashboardLayout,
});

function PetDropdownItem({
  pet,
  isActive,
  colors,
  setSelectedPetId,
}: {
  pet: Pet;
  isActive: boolean;
  colors: Record<string, string>;
  setSelectedPetId: (id: string) => void;
}) {
  const { signedImageUrl } = usePetPhoto({
    petId: pet.id,
    photoKey: pet.photoKey,
    imageUrl: pet.imageUrl,
  });

  return (
    <DropdownMenuItem
      onClick={() => setSelectedPetId(pet.id)}
      className="cursor-pointer"
      style={{
        backgroundColor: isActive ? colors.surfaceHover : 'transparent',
      }}
    >
      <div className="flex items-center gap-3 w-full">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-lg overflow-hidden"
          style={{
            backgroundColor: isActive
              ? colors.primary + '20'
              : colors.surfaceHover,
          }}
        >
          {signedImageUrl ? (
            <img
              src={signedImageUrl}
              alt={pet.name}
              className="w-full h-full object-cover"
            />
          ) : pet.species === 'dog' ? (
            '🐕'
          ) : (
            '🐈'
          )}
        </div>
        <div className="flex-1 text-left">
          <div className="text-sm font-medium" style={{ color: colors.text }}>{pet.name}</div>
          <div className="text-xs" style={{ color: colors.textSecondary }}>
            {pet.breed}
          </div>
        </div>
        {isActive && (
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: colors.primary }}
          />
        )}
      </div>
    </DropdownMenuItem>
  );
}

const SOCIAL_PLATFORMS = ['instagram', 'facebook', 'tiktok', 'x'] as const;

function PetsoDashboardLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const location = useRouterState({ select: (s) => s.location });
  const { t, i18n } = useTranslation();
  const currentLang = (() => {
    const raw = i18n.resolvedLanguage || i18n.language || '';
    const base = raw.split('-')[0].toLowerCase();
    return (
      ['en', 'pl', 'es', 'nl', 'fr', 'de', 'it'].includes(base) ? base : 'en'
    ) as Language;
  })();
  const handleLanguageChange = (lang: Language) => {
    void i18n.changeLanguage(lang);
  };
  const { user, logout, isAuthenticated, isLoading } = useAuth();
  const {
    pets,
    selectedPetId,
    selectedPet,
    setSelectedPetId,
    petDraft,
    hasDraft,
    refreshDraft,
  } = usePets();
  const { connectPlatform } = useSocialConnections();
  const { colors, themePreference, setTheme } = useTheme();
  const { isOnboardingComplete } = useOwnerOnboarding();
  const { unreadCount } = useNotifications();
  const [isAddPetOpen, setIsAddPetOpen] = useState(false);
  const [isQuickAddPetsOpen, setIsQuickAddPetsOpen] = useState(false);
  const [isDraftOpen, setIsDraftOpen] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const openNotificationPanel = () => setIsNotificationPanelOpen(true);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const closeMobileDrawer = () => setIsMobileDrawerOpen(false);

  useEffect(() => {
    if (pathname) setIsMobileDrawerOpen(false);
  }, [pathname]);

  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    if (typeof localStorage === 'undefined') return true;
    return localStorage.getItem('petso-notifications-enabled') !== 'false';
  });

  const handleNotificationsEnabledChange = (checked: boolean) => {
    setNotificationsEnabled(checked);
    localStorage.setItem('petso-notifications-enabled', String(checked));
  };

  // Lock body scroll so only the main content area scrolls (avoids "duplicate page" scroll bug)
  useEffect(() => {
    document.documentElement.classList.add('dashboard-active');
    document.body.classList.add('dashboard-active');
    return () => {
      document.documentElement.classList.remove('dashboard-active');
      document.body.classList.remove('dashboard-active');
    };
  }, []);

  // OAuth callback: after provider redirects to e.g. /dashboard?social=connected&platform=instagram
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const social = params.get('social');
    const platform = params.get('platform');
    if (
      social === 'connected' &&
      platform &&
      SOCIAL_PLATFORMS.includes(platform as (typeof SOCIAL_PLATFORMS)[number])
    ) {
      connectPlatform(platform as (typeof SOCIAL_PLATFORMS)[number]);
      toast.success(
        t('drawer.platformConnected', {
          platform: platform.charAt(0).toUpperCase() + platform.slice(1),
        })
      );
      window.history.replaceState(
        null,
        '',
        pathname || window.location.pathname
      );
    }
  }, [location.search, pathname, connectPlatform]);

  const { signedImageUrl } = usePetPhoto({
    petId: selectedPet?.id || '',
    photoKey: selectedPet?.photoKey,
    imageUrl: selectedPet?.imageUrl,
  });

  const handleDraftClick = () => {
    setIsDraftOpen(true);
  };

  const handleDraftClose = () => {
    setIsDraftOpen(false);
    // Refresh draft state in case it was completed or saved
    refreshDraft();
  };

  // Show loading state while auth is being determined
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F6FA] dark:bg-[#0B0A12]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#594BFF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p
            className="text-[#464452] dark:text-white/70"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    void navigate({ to: '/login' }).catch(() => {
      // Router may reject in edge cases; avoid unhandled "Uncaught (in promise) undefined"
    });
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate({ to: '/' });
  };

  const handleAddPetClick = () => {
    setIsAddPetOpen(true);
  };

  type SidebarTab = {
    id: string;
    path: string;
    labelKey: string;
    icon: typeof Home;
    soon: boolean;
    requiresPet?: boolean;
    requiresPetType?: boolean;
  };

  const tabs: SidebarTab[] = [
    {
      id: 'overview',
      path: '/dashboard',
      labelKey: 'navigation.overview',
      icon: Home,
      soon: false,
    },
    {
      id: 'epassport',
      path: '/dashboard/epassport',
      labelKey: 'navigation.epassport',
      icon: Plane,
      soon: false,
    },
    {
      id: 'documents',
      path: '/dashboard/documents',
      labelKey: 'navigation.documents',
      icon: FileText,
      soon: false,
    },
    {
      id: 'family',
      path: '/dashboard/family',
      labelKey: 'navigation.familyTree',
      icon: Share2,
      soon: false,
    },
    {
      id: 'calendar',
      path: '/dashboard/calendar',
      labelKey: 'navigation.calendar',
      icon: CalendarDays,
      soon: false,
    },
    // "Know Your Pet" - visible only after a pet exists, enabled only when a pet type is selected
    ...(pets.length > 0
      ? ([
          {
            id: 'knowYourPet',
            path: '/dashboard/know-your-pet',
            labelKey: 'navigation.knowYourPet',
            icon: PawPrint,
            soon: false,
            requiresPet: true,
            requiresPetType: true,
          },
        ] satisfies SidebarTab[])
      : []),
    {
      id: 'petParks',
      path: '/dashboard/pet-parks',
      labelKey: 'navigation.petParks',
      icon: MapPin,
      soon: false,
    },
    {
      id: 'social',
      path: '/dashboard/social',
      labelKey: 'navigation.social',
      icon: Users,
      soon: false,
    },
    {
      id: 'training',
      path: '/dashboard/training',
      labelKey: 'navigation.training',
      icon: GraduationCap,
      soon: false,
    },
    {
      id: 'rewards',
      path: '/dashboard/rewards',
      labelKey: 'navigation.rewards',
      icon: Gift,
      soon: false,
    },
    {
      id: 'appointments',
      path: '/dashboard/appointments',
      labelKey: 'navigation.appointments',
      icon: Calendar,
      soon: false,
    },
  ];

  const isPetParksRoute = pathname.startsWith('/dashboard/pet-parks');

  return (
    <AddPetModalProvider openAddPet={handleAddPetClick}>
      <NotificationPanelProvider
        isOpen={isNotificationPanelOpen}
        onOpenChange={setIsNotificationPanelOpen}
      >
        <div
          id="petso-dashboard-layout"
          className={`dashboard-layout h-screen flex overflow-hidden min-w-0 w-full ${isMobileDrawerOpen ? 'dashboard-drawer-open' : ''}`}
          style={{ backgroundColor: colors.background }}
        >
          <NotificationSidePanel
            open={isNotificationPanelOpen}
            onOpenChange={setIsNotificationPanelOpen}
            onNavigateTo={(notification: Notification) => {
              if (
                notification.actionLink === 'pending-approvals' &&
                notification.petId
              ) {
                setSelectedPetId(notification.petId);
                navigate({ to: '/dashboard/family' });
              }
              if (notification.source === 'Rewards') {
                navigate({ to: '/dashboard/rewards' });
              }
              if (notification.source === 'Calendar') {
                navigate({ to: '/dashboard/calendar' });
              }
              if (notification.source === 'Documents') {
                navigate({ to: '/dashboard/documents' });
              }
              if (
                notification.source === 'Overview' ||
                notification.source === 'E-Passport'
              ) {
                if (notification.petId) {
                  setSelectedPetId(notification.petId);
                  navigate({ to: '/dashboard' });
                }
              }
              if (notification.source === 'Family Tree' && notification.petId) {
                setSelectedPetId(notification.petId);
                navigate({ to: '/dashboard/family' });
              }
              // Booking notifications → navigate to calendar/appointments
              const nType = (notification.type || '').toLowerCase();
              if (nType.includes('booking') || nType.includes('appointment')) {
                if (notification.petId) setSelectedPetId(notification.petId);
                navigate({ to: '/dashboard/calendar' });
              }
            }}
          />

          {/* Mobile header: visibility controlled by CSS (index.css) */}
          <header
            className="dashboard-mobile-header"
            style={{
              backgroundColor: colors.surface,
              borderBottom: `1px solid ${colors.border}`,
            }}
          >
            <button
              type="button"
              onClick={() => setIsMobileDrawerOpen(true)}
              className="p-2 rounded-lg touch-manipulation"
              style={{ color: colors.text }}
              aria-label={t('common.menu', 'Menu')}
            >
              <Menu className="w-6 h-6" />
            </button>
            <img
              src={petsoLogo}
              alt="Petso"
              className="h-7 flex-shrink-0"
              style={{ objectFit: 'contain' }}
            />
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setIsNotificationPanelOpen(true)}
                className="relative flex items-center justify-center w-11 h-11 rounded-lg touch-manipulation"
                style={{
                  color: colors.text,
                  backgroundColor: colors.surfaceHover,
                }}
                aria-label={t('navigation.notifications', 'Notifications')}
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 text-[10px] font-semibold min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-white bg-red-500">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            </div>
          </header>

          {/* Backdrop - only on mobile when sidebar open (visibility via CSS) */}
          <div
            id="dashboard-drawer-backdrop"
            className="dashboard-drawer-backdrop"
            onClick={closeMobileDrawer}
            onKeyDown={(e) => e.key === 'Escape' && closeMobileDrawer()}
            role="button"
            tabIndex={0}
            aria-hidden="true"
          />

          {/* Sidebar: position/visibility controlled by CSS on mobile */}
          <div
            id="dashboard-sidebar"
            className="dashboard-sidebar w-64 flex flex-col flex-shrink-0 overflow-hidden"
            style={{
              backgroundColor: colors.surface,
              borderRight: `1px solid ${colors.border}`,
            }}
          >
            {/* Close button - visible only on mobile (CSS) */}
            <button
              type="button"
              onClick={closeMobileDrawer}
              className="dashboard-sidebar-close absolute top-4 right-4 p-2 rounded-lg z-10"
              style={{ color: colors.text }}
              aria-label={t('common.close', 'Close')}
            >
              <X className="w-5 h-5" />
            </button>

            <div
              className="p-6 flex-shrink-0"
              style={{ borderBottom: `1px solid ${colors.border}` }}
            >
              <div className="flex items-center justify-between mb-6">
                <img
                  src={petsoLogo}
                  alt="Petso"
                  className="h-8 cursor-pointer"
                  style={{ objectFit: 'contain' }}
                />

                {/* User Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors hover:bg-[#594BFF]/10 cursor-pointer"
                      style={{ color: colors.text }}
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{
                          background:
                            'linear-gradient(135deg, #594BFF 0%, #8B7FFF 100%)',
                        }}
                      >
                        {user?.imageUrl ? (
                          <img
                            src={user.imageUrl}
                            alt={user.name || 'Profile'}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <User className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <ChevronDown
                        className="w-4 h-4"
                        style={{ color: colors.textSecondary }}
                      />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-64 dashboard-profile-dropdown"
                    style={{
                      backgroundColor: colors.surface,
                      borderColor: colors.border,
                    }}
                  >
                    <DropdownMenuLabel>
                      <div className="flex flex-col space-y-1">
                        <p
                          className="text-sm font-medium"
                          style={{ color: colors.text }}
                        >
                          {user?.name || t('common.user', 'User')}
                        </p>
                        <p
                          className="text-xs"
                          style={{ color: colors.textSecondary }}
                        >
                          {user?.email || ''}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => navigate({ to: '/dashboard/settings' })}
                      className="cursor-pointer"
                    >
                      <User className="w-4 h-4 mr-2" />
                      {t('drawer.myProfile')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate({ to: '/dashboard/settings' })}
                      className="cursor-pointer"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      {t('drawer.settings')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel
                      className="text-xs font-medium"
                      style={{ color: colors.textSecondary }}
                    >
                      {t('drawer.notifications')}
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      onSelect={(e) => e.preventDefault()}
                      className="cursor-default focus:bg-transparent"
                    >
                      <span
                        className="flex-1 text-sm"
                        style={{ color: colors.text }}
                      >
                        {t('drawer.enableNotifications')}
                      </span>
                      <Switch
                        checked={notificationsEnabled}
                        onCheckedChange={handleNotificationsEnabledChange}
                      />
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={openNotificationPanel}
                      className="cursor-pointer"
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      {t('drawer.viewNotifications')}
                      {unreadCount > 0 && (
                        <span
                          className="ml-auto text-xs font-semibold tabular-nums rounded-full min-w-[1.25rem] h-5 flex items-center justify-center px-1.5"
                          style={{
                            backgroundColor: colors.primary + '20',
                            color: colors.primary,
                          }}
                        >
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuLabel
                      className="text-xs font-medium pt-1"
                      style={{ color: colors.textSecondary }}
                    >
                      {t('language.selectLanguage')}
                    </DropdownMenuLabel>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="cursor-pointer">
                        <span
                          className="text-sm"
                          style={{ color: colors.text }}
                        >
                          {LANGUAGE_LABELS[currentLang]}
                        </span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent
                        style={{
                          backgroundColor: colors.surface,
                          borderColor: colors.border,
                        }}
                      >
                        <DropdownMenuRadioGroup
                          value={currentLang}
                          onValueChange={(v) =>
                            handleLanguageChange(v as Language)
                          }
                        >
                          {(
                            Object.keys(LANGUAGES) as Array<
                              keyof typeof LANGUAGES
                            >
                          ).map((key) => {
                            const lang = LANGUAGES[key];
                            return (
                              <DropdownMenuRadioItem
                                key={lang}
                                value={lang}
                                className="cursor-pointer"
                              >
                                {LANGUAGE_LABELS[lang]}
                              </DropdownMenuRadioItem>
                            );
                          })}
                        </DropdownMenuRadioGroup>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuLabel
                      className="text-xs font-medium pt-1"
                      style={{ color: colors.textSecondary }}
                    >
                      {t('drawer.appearance')}
                    </DropdownMenuLabel>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="cursor-pointer">
                        <span
                          className="text-sm"
                          style={{ color: colors.text }}
                        >
                          {themePreference === 'light'
                            ? t('drawer.lightMode')
                            : themePreference === 'dark'
                              ? t('drawer.darkMode')
                              : t('drawer.systemDefault')}
                        </span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent
                        style={{
                          backgroundColor: colors.surface,
                          borderColor: colors.border,
                        }}
                      >
                        <DropdownMenuRadioGroup
                          value={themePreference}
                          onValueChange={(v) => setTheme(v as ThemePreference)}
                        >
                          <DropdownMenuRadioItem
                            value="light"
                            className="cursor-pointer"
                          >
                            <Sun className="w-4 h-4 mr-2" />
                            {t('drawer.lightMode')}
                          </DropdownMenuRadioItem>
                          <DropdownMenuRadioItem
                            value="dark"
                            className="cursor-pointer"
                          >
                            <Moon className="w-4 h-4 mr-2" />
                            {t('drawer.darkMode')}
                          </DropdownMenuRadioItem>
                          <DropdownMenuRadioItem
                            value="system"
                            className="cursor-pointer"
                          >
                            <Monitor className="w-4 h-4 mr-2" />
                            {t('drawer.systemDefault')}
                          </DropdownMenuRadioItem>
                        </DropdownMenuRadioGroup>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('drawer.logOut')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="space-y-3">
                <div
                  className="text-sm"
                  style={{ color: colors.textSecondary }}
                >
                  {t('drawer.myPets')}
                </div>

                {/* Pet Selection Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200 border cursor-pointer"
                      style={{
                        backgroundColor: colors.surfaceHover,
                        borderColor: colors.border,
                        color: colors.text,
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.borderColor = colors.primary;
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = colors.border;
                      }}
                    >
                      {selectedPet ? (
                        <>
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center text-xl overflow-hidden"
                            style={{
                              backgroundColor: colors.primary + '20',
                              boxShadow: `0 0 0 2px ${colors.primary}`,
                            }}
                          >
                            {signedImageUrl ? (
                              <img
                                src={signedImageUrl}
                                alt={selectedPet.name}
                                className="w-full h-full object-cover"
                              />
                            ) : selectedPet.species === 'dog' ? (
                              '🐕'
                            ) : (
                              '🐈'
                            )}
                          </div>
                          <div className="flex-1 text-left">
                            <div className="text-sm font-medium">
                              {selectedPet.name}
                            </div>
                            <div
                              className="text-xs"
                              style={{ color: colors.textSecondary }}
                            >
                              {selectedPet.breed}
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: colors.surfaceHover }}
                          >
                            <PawPrint
                              className="w-5 h-5"
                              style={{ color: colors.textSecondary }}
                            />
                          </div>
                          <div className="flex-1 text-left">
                            <div
                              className="text-sm"
                              style={{ color: colors.textSecondary }}
                            >
                              {t('drawer.selectPet', 'Select a pet')}
                            </div>
                          </div>
                        </>
                      )}
                      <ChevronDown
                        className="w-4 h-4"
                        style={{ color: colors.textSecondary }}
                      />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="w-56 max-h-[300px] overflow-y-auto"
                    style={{
                      backgroundColor: colors.surface,
                      borderColor: colors.border,
                    }}
                  >
                    {pets.length === 0 ? (
                      <DropdownMenuItem disabled>
                        <span
                          className="text-sm"
                          style={{ color: colors.textSecondary }}
                        >
                          {t('drawer.noPets', 'No pets yet')}
                        </span>
                      </DropdownMenuItem>
                    ) : (
                      pets.map((pet) => {
                        const isActive = selectedPetId === pet.id;
                        return (
                          <PetDropdownItem
                            key={pet.id}
                            pet={pet}
                            isActive={isActive}
                            colors={colors}
                            setSelectedPetId={(id) => {
                              setSelectedPetId(id);
                              closeMobileDrawer();
                            }}
                          />
                        );
                      })
                    )}

                    {/* Draft Pet in dropdown */}
                    {hasDraft && petDraft && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => {
                            handleDraftClick();
                            closeMobileDrawer();
                          }}
                          className="cursor-pointer"
                        >
                          <div className="flex items-center gap-3 w-full">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center"
                              style={{
                                backgroundColor: colors.surfaceHover,
                                opacity: 0.7,
                              }}
                            >
                              <FileEdit
                                className="w-4 h-4"
                                style={{ color: colors.textSecondary }}
                              />
                            </div>
                            <div className="flex-1 text-left">
                              <div className="text-sm font-medium flex items-center gap-2">
                                {petDraft.petName ||
                                  t('common.unnamedPet', 'Unnamed Pet')}
                                <span
                                  className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase"
                                  style={{
                                    backgroundColor: '#F59E0B20',
                                    color: '#D97706',
                                  }}
                                >
                                  {t('drawer.draft')}
                                </span>
                              </div>
                              <div
                                className="text-xs"
                                style={{ color: colors.textSecondary }}
                              >
                                {petDraft.species ||
                                  t('drawer.continueEditing')}
                              </div>
                            </div>
                          </div>
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Add Pet + Quick add multiple */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      handleAddPetClick();
                      closeMobileDrawer();
                    }}
                    className="flex-1 flex items-center gap-2 p-3 rounded-lg border-2 border-dashed transition-colors cursor-pointer"
                    style={{
                      borderColor: colors.border,
                      color: colors.textSecondary,
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = colors.primary;
                      e.currentTarget.style.backgroundColor =
                        colors.surfaceHover;
                      e.currentTarget.style.color = colors.primary;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor = colors.border;
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = colors.textSecondary;
                    }}
                  >
                    <Plus className="w-5 h-5" />
                    <span className="text-sm">{t('drawer.addPet')}</span>
                  </button>
                  <button
                    onClick={() => {
                      setIsQuickAddPetsOpen(true);
                      closeMobileDrawer();
                    }}
                    className="p-3 rounded-lg border-2 border-dashed transition-colors shrink-0 cursor-pointer"
                    style={{
                      borderColor: colors.border,
                      color: colors.textSecondary,
                    }}
                    title={t('drawer.quickAddPets', 'Quick add multiple pets')}
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = colors.primary;
                      e.currentTarget.style.backgroundColor =
                        colors.surfaceHover;
                      e.currentTarget.style.color = colors.primary;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor = colors.border;
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = colors.textSecondary;
                    }}
                  >
                    <Users className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            <nav className="flex-1 overflow-y-auto p-4 min-h-0">
              {tabs.map((tab) => {
                const isLocked =
                  !isOnboardingComplete &&
                  tab.id !== 'overview' &&
                  tab.id !== 'settings';
                const isSoon = tab.soon;
                const isPetTypeMissing =
                  !!tab.requiresPetType && !selectedPet?.species;
                const isDisabled = isLocked || isSoon || isPetTypeMissing;
                return (
                  <Fragment key={tab.id}>
                    <Link
                      to={tab.path}
                      onClick={(e) => {
                        closeMobileDrawer();
                        if (isLocked) {
                          e.preventDefault();
                          navigate({ to: '/dashboard/settings' });
                        } else if (isSoon) {
                          e.preventDefault();
                          toast.info(t('common.comingSoon'));
                        } else if (isPetTypeMissing) {
                          e.preventDefault();
                          toast.info(t('knowYourPet.toast.selectPetType'));
                        }
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors relative"
                      activeOptions={{ exact: tab.id === 'overview' }}
                      activeProps={{
                        style: isSoon
                          ? {
                              background: 'transparent',
                              color: colors.text,
                              opacity: 0.5,
                              cursor: 'not-allowed',
                            }
                          : {
                              background:
                                'linear-gradient(135deg, #594BFF 0%, #8B7FFF 100%)',
                              color: '#FFFFFF',
                            },
                      }}
                      inactiveProps={{
                        style: {
                          background: 'transparent',
                          color: colors.text,
                          opacity: isDisabled ? 0.5 : 1,
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                        },
                      }}
                    >
                      <tab.icon className="w-5 h-5" />
                      <span>{t(tab.labelKey)}</span>
                      {isLocked && <Lock className="w-4 h-4 ml-auto" />}
                      {!isLocked && isSoon && (
                        <span className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
                          {t('common.soon')}
                        </span>
                      )}
                    </Link>
                  </Fragment>
                );
              })}
            </nav>
          </div>

          {/* Main Content: padding-top for mobile header (CSS), flex-1 + min-h-0 to scroll */}
          <div className="dashboard-main flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col min-w-0">
            <PetInvitationsBanner />
            <div
              className={
                isPetParksRoute
                  ? 'h-full p-0 flex-1'
                  : 'p-4 sm:p-6 md:p-8 flex-1 min-w-0'
              }
            >
              <SocialFeedProvider>
                <Outlet />
              </SocialFeedProvider>
            </div>
          </div>

          {isAddPetOpen && (
            <AddPetModal onClose={() => setIsAddPetOpen(false)} />
          )}
          {isQuickAddPetsOpen && (
            <QuickAddPetsModal
              onClose={() => setIsQuickAddPetsOpen(false)}
              onSuccess={() => setIsQuickAddPetsOpen(false)}
            />
          )}
          {isDraftOpen && <PetOnboarding onClose={handleDraftClose} />}
        </div>
      </NotificationPanelProvider>
    </AddPetModalProvider>
  );
}
